import DashboardIcon from '@mui/icons-material/Dashboard';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Card, CardContent, CardMedia, Container, Grid, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useInView } from 'react-intersection-observer';

export default function ScreenshotsSection() {
    const theme = useTheme();
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const screenshots = [
        {
            title: 'Dashboard',
            description: 'Tüm mağazalarınızın performansını tek bir ekranda görüntüleyin ve analiz edin.',
            image: 'https://i.hizliresim.com/498hc2y.PNG',
            icon: <DashboardIcon fontSize="large" />,
            emoji: '📊',
            color: '#4CAF50',
        },
        {
            title: 'Ürünler',
            description: 'Tüm ürünlerinizi yönetin ve ürün bazlı soru-cevap performansını takip edin.',
            image: 'https://i.hizliresim.com/hxhsl3m.png',
            icon: <ShoppingCartIcon fontSize="large" />,
            emoji: '🛍️',
            color: '#2196F3',
        },
        {
            title: 'Sorular',
            description: 'Tüm müşteri sorularını görüntüleyin, filtreleme yapın ve yanıtları yönetin.',
            image: 'https://i.hizliresim.com/5mqncvb.png',
            icon: <QuestionAnswerIcon fontSize="large" />,
            emoji: '💬',
            color: '#FF9800',
        },
        {
            title: 'Onay Bekleyen Yanıtlar',
            description: 'Yapay zekanın emin olmadığı yanıtları kontrol edin ve onaylayın.',
            image: 'https://i.hizliresim.com/270eha5.png',
            icon: <FactCheckIcon fontSize="large" />,
            emoji: '✅',
            color: '#F44336',
        }
    ];

    return (
        <Box
            sx={{
                py: 10,
                background: 'white',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                fontWeight: 700,
                                mb: 2,
                                color: theme.palette.secondary.main,
                            }}
                        >
                            Helpigo Paneli
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                color: theme.palette.text.secondary,
                                maxWidth: '800px',
                                mx: 'auto',
                                mb: 4,
                            }}
                        >
                            Kullanıcı dostu arayüzümüz ile tüm mağazalarınızı ve müşteri sorularını kolayca yönetin.
                        </Typography>
                    </motion.div>
                </Box>

                <Grid container spacing={4} ref={ref}>
                    {screenshots.map((screenshot, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Card
                                    elevation={3}
                                    sx={{
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.3s',
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            height: 250,
                                            backgroundColor: `${screenshot.color}15`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Ekran görüntüsü */}
                                        <CardMedia
                                            component="img"
                                            height="250"
                                            image={screenshot.image}
                                            alt={screenshot.title}
                                            sx={{
                                                objectFit: 'contain',
                                                p: 2,
                                                width: '100%',
                                            }}
                                            onError={(e) => {
                                                console.error(`Resim yüklenemedi: ${screenshot.image}`);
                                                // Resim yüklenemezse emoji göster
                                                e.target.style.display = 'none';
                                                const parent = e.target.parentElement;
                                                if (parent) {
                                                    const fallback = document.createElement('div');
                                                    fallback.style.textAlign = 'center';
                                                    fallback.innerHTML = `
                                                        <div style="font-size: 7rem; line-height: 1; margin-bottom: 16px;">${screenshot.emoji || '🖼️'}</div>
                                                        <div style="color: ${screenshot.color}; font-weight: bold; text-shadow: 0 2px 10px rgba(0,0,0,0.1);">${screenshot.title} Ekranı</div>
                                                    `;
                                                    parent.appendChild(fallback);
                                                }
                                            }}
                                        />
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Box
                                                sx={{
                                                    mr: 1,
                                                    color: screenshot.color,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {screenshot.icon}
                                            </Box>
                                            <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                                                {screenshot.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {screenshot.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
} 